import React, { useContext, useState } from 'react';
import {
  Tooltip, Accordion, AccordionSummary, AccordionDetails,
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField
} from '@mui/material';
import { NotificationContext } from '../context/Notification.js';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './EditCalculator.css';
import { FirebaseService } from '../../services/FirebaseService.js';
import { collections } from '../../constants.js';
import { Spinner } from '../Spinner.js';
import { CalcsContext } from '../context/Calcs.js';

const EditCalculator = ({ title, calculator, itemList, onFinish }) => {
  const { setNotification } = useContext(NotificationContext);
  const { itemMap, setItemMap } = useContext(CalcsContext);
  const fire = new FirebaseService(null, setNotification);
  const [expanded, setExpanded] = useState('');
  const columns = [
    { name: 'Name', field: 'name', flex: '3 1 0' },
    { name: 'Value', field: 'price', flex: '2 2 0' },
    { name: 'Action', field: null, flex: '1 3 0' }
  ];
  const [loading, setLoading] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [editField, setEditField] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  function expandField(id) {
    if (expanded === id) {
      setExpanded('');
    } else {
      setExpanded(id);
    }
  }

  function openDialog(field, item) {
    setEditField(field);
    setEditItem(item);
    setShowEditDialog(true);
  }

  function changeItem(value, field) {
    let newItem = editItem;
    if (field.match(/(upper|lower)(Value)/)) {
      if(!editItem.lowerValue || !editItem.upperValue) {
        newItem.name = newItem.name.replace(/(\d+)/, newItem[field]);
      } else {
        newItem.name = newItem.name.replace(newItem[field], value);
      }
    }

    let type = typeof editItem[field];
    newItem[field] = type === 'number' ? Number(value) : value;
    setEditItem({ ...newItem });
    setDisableSave(value.length === 0);
  }

  function deleteItem() {
    setLoading(true);
    fire.deleteDocument(collections.items, `${editField.collection}-${editItem.id}`, editItem).then(val => {
      let list = itemMap[calculator.id][editField.collection].filter(i => i.id !== editItem.id);
      setItemMap({...itemMap, [calculator.id]: {...itemMap[calculator.id], [editField.collection]: list}});
      setLoading(false);
    });
    closeDialog(false);
  }

  function confirmDelete(field, item) {
    setEditField(field);
    setEditItem(item);
    setShowConfirmDialog(true);
  }

  function closeDialog(saveChanges) {
    if (saveChanges) {
      setLoading(true);
      fire.updateDocument(collections.items, `${editField.collection}-${editItem.id}`, editItem).then(val => {
        let list = itemMap[calculator.id][editField.collection];
        let index = list.findIndex(l => l.id === editItem.id);
        list[index] = editItem;
        setItemMap({ ...itemMap, [calculator.id]: { ...itemMap[calculator.id], [editField.collection]: list } });
        setLoading(false);
      });
    }
    setShowConfirmDialog(false);
    setShowEditDialog(false);
    setEditField(null);
    setEditItem(null);
  }

  return (
    <div>
      {calculator && itemList &&
        <div>
          <Button variant='outlined' onClick={onFinish}>
            Done
          </Button>
          <h1>{title}</h1>
          <div>
            {calculator.fields?.length &&
              calculator.fields.map((field, idx) => {
                if (calculator[field.id].collection) {
                  return (
                    <Accordion expanded={expanded === field.id} onChange={() => expandField(field.id)} key={field.id}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        {field.name}
                      </AccordionSummary>

                      <AccordionDetails>
                        <span>Options</span>
                        <TableContainer component={Paper} sx={{ maxHeight: '50vh', overflow: 'auto' }}>
                          <Table>
                            <TableHead sx={{ zIndex: 3, position: 'sticky', top: 0, backgroundColor: 'white' }}>
                              <TableRow sx={{ display: 'flex' }}>
                                {
                                  columns.map((col, idx) => {
                                    return (
                                      <TableCell align='left' sx={{ flex: col.flex }} key={idx}>
                                        {col.name}
                                      </TableCell>
                                    )
                                  })
                                }
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {
                                itemList[field.collection].map(item => {
                                  return (
                                    <TableRow sx={{ display: 'flex' }} key={item.id}>
                                      {
                                        columns.map((col, idx) => {
                                          return (
                                            <TableCell align='left' sx={{ flex: col.flex }} key={idx}>
                                              {col.field &&
                                                <span>{item[col.field]}</span>
                                              }
                                              {!col.field &&
                                                <div>
                                                  <Tooltip title='Edit' placement='left'>
                                                    <EditOutlinedIcon sx={{ fontSize: 12, cursor: 'pointer', marginRight: '15px' }} onClick={() => openDialog(field, item)} />
                                                  </Tooltip>
                                                  <Tooltip title='Delete' placement='right'>
                                                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 12, cursor: 'pointer' }} onClick={() => confirmDelete(field, item)} />
                                                  </Tooltip>
                                                </div>
                                              }
                                            </TableCell>
                                          )
                                        })
                                      }
                                    </TableRow>
                                  )
                                })
                              }
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )
                } else {
                  return (
                    <div key={`${field.id}-${idx}`}></div>
                  )
                }
              })
            }
          </div>

          <Dialog open={showEditDialog && editField !== undefined && editItem !== undefined}
            sx={{ width: '50vw', height: '50vh', top: '25%', left: '25%', zIndex: 9 }}
            PaperProps={{ sx: { width: '100%', height: '100%', maxHeight: '100%', margin: 0 } }}
            onClose={(event, reason) => closeDialog(false)}
          >
            <DialogTitle>
              {editField?.name}
            </DialogTitle>

            <DialogContent dividers>
              <div>
                {(editItem?.upperValue || editItem?.lowerValue) &&
                  <div>
                    <div className='row'>
                      <span style={{ marginRight: '10px' }}>Item Name:</span>
                      <span>{editItem?.name}</span>
                    </div>
                    {editItem.lowerValue &&
                      <div className='row'>
                        <span style={{ marginRight: '10px' }}>Lower Value:</span>
                        <TextField label=''
                          variant='outlined'
                          type='number'
                          sx={{ '& .MuiOutlinedInput-input': { padding: '5px' } }}
                          value={editItem?.lowerValue}
                          onChange={(e) => changeItem(e.target.value, 'lowerValue')}>
                        </TextField>
                      </div>
                    }
                    {editItem.upperValue &&
                      <div className='row'>
                        <span style={{ marginRight: '10px' }}>Upper Value:</span>
                        <TextField label=''
                          variant='outlined'
                          type='number'
                          sx={{ '& .MuiOutlinedInput-input': { padding: '5px' } }}
                          value={editItem?.upperValue}
                          onChange={(e) => changeItem(e.target.value, 'upperValue')}>
                        </TextField>
                      </div>
                    }
                  </div>
                }
                {!editItem?.upperValue && !editItem?.lowerValue &&
                  <div className='row'>
                    <span style={{ marginRight: '10px' }}>Item Name:</span>
                    <TextField label=''
                      variant='outlined'
                      type='text'
                      sx={{ '& .MuiOutlinedInput-input': { padding: '5px' } }}
                      value={editItem?.name}
                      onChange={(e) => changeItem(e.target.value, 'name')}>
                    </TextField>
                  </div>
                }
                <div className='row'>
                  <span style={{ marginRight: '10px' }}>Item Value:</span>
                  <TextField label=''
                    variant='outlined'
                    type='number'
                    sx={{ '& .MuiOutlinedInput-input': { padding: '5px' } }}
                    value={editItem?.price}
                    onChange={(e) => changeItem(e.target.value, 'price')}>
                  </TextField>
                </div>
              </div>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => closeDialog(false)}>
                Cancel
              </Button>
              <Button onClick={() => closeDialog(true)} disabled={disableSave}>
                Submit
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={showConfirmDialog} onClose={() => closeDialog(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>Are you sure you want to delete this item?</DialogContent>
            <DialogActions>
              <Button onClick={deleteItem}>Yes</Button>
              <Button onClick={() => closeDialog(false)}>No</Button>

            </DialogActions>
          </Dialog>
        </div>
      }

      {loading &&
        <Spinner isLoading={loading}></Spinner>
      }
    </div>
  )
}

export default EditCalculator;